import { Component } from '@theme/component';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const DEFAULT_CURRENCY_DECIMALS = 2;

/** @type {Record<string, number>} */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

/**
 * @typedef {Object} PriceSliderRefs
 * @property {HTMLInputElement} minRange
 * @property {HTMLInputElement} maxRange
 * @property {HTMLElement} rangeFill
 * @property {HTMLElement} minLabel
 * @property {HTMLElement} maxLabel
 */

/**
 * Dual-thumb price range slider that syncs with hidden price facet inputs.
 *
 * @extends {Component<PriceSliderRefs>}
 */
class PriceSliderComponent extends Component {
  /** @type {string} */
  #currency = 'USD';

  /** @type {string} */
  #displayMoneyFormat = '{{amount}}';

  /** @type {string} */
  #inputMoneyFormat = '{{amount}}';

  /** @type {number} */
  #rangeMin = 0;

  /** @type {number} */
  #rangeMax = 0;

  /** @type {number} */
  #step = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#readConfig();
    this.#applyRangeAttributes();
    this.#initializeThumbValues();
    this.#updateUI();

    const { minRange, maxRange } = this.refs;
    minRange.addEventListener('input', this.#onRangeInput);
    maxRange.addEventListener('input', this.#onRangeInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { minRange, maxRange } = this.refs;
    minRange.removeEventListener('input', this.#onRangeInput);
    maxRange.removeEventListener('input', this.#onRangeInput);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#readConfig();
    this.#applyRangeAttributes();
    this.#initializeThumbValues();
    this.#updateUI();
  }

  /**
   * Handles range thumb commit. Routed via on:change on this element so
   * price-facet-component does not receive the range input change directly.
   */
  handleSliderChange() {
    this.#constrainThumbs();
    this.#updateHiddenInputs(true);
    this.#updateUI();
  }

  #readConfig() {
    this.#currency = this.dataset.currency ?? 'USD';
    this.#displayMoneyFormat = this.dataset.moneyFormat ?? '{{amount}}';
    this.#inputMoneyFormat = this.#extractMoneyPlaceholder(this.#displayMoneyFormat);
    this.#step = this.#getStep();
    this.#rangeMin = this.#snapToStep(
      this.#minorToMajor(parseInt(this.dataset.rangeMinMinor ?? '0', 10) || 0)
    );
    this.#rangeMax = this.#snapToStep(
      this.#minorToMajor(parseInt(this.dataset.rangeMaxMinor ?? '0', 10) || 0)
    );
  }

  #applyRangeAttributes() {
    const { minRange, maxRange } = this.refs;

    for (const range of [minRange, maxRange]) {
      range.min = String(this.#rangeMin);
      range.max = String(this.#rangeMax);
      range.step = String(this.#step);
    }
  }

  /**
   * @param {string} format
   * @returns {string}
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * @returns {number}
   */
  #getCurrencyPrecision() {
    return CURRENCY_DECIMALS[this.#currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;
  }

  /**
   * @returns {number}
   */
  #getStep() {
    return 1;
  }

  /**
   * Snaps a value to the nearest valid step for range inputs.
   * @param {number} value
   * @returns {number}
   */
  #snapToStep(value) {
    return Math.round(value / this.#step) * this.#step;
  }

  /**
   * @param {number} minorUnits
   * @returns {number}
   */
  #minorToMajor(minorUnits) {
    const divisor = Math.pow(10, this.#getCurrencyPrecision());
    return minorUnits / divisor;
  }

  /**
   * @param {string} value
   * @returns {number}
   */
  #parseMajorUnits(value) {
    const minor = convertMoneyToMinorUnits(value, this.#currency);
    if (minor === null) return 0;

    return this.#minorToMajor(minor);
  }

  /**
   * @param {number} majorUnits
   * @returns {number}
   */
  #toMinorUnits(majorUnits) {
    const precision = this.#getCurrencyPrecision();
    return Math.round(majorUnits * Math.pow(10, precision));
  }

  /**
   * @returns {{ minInput: HTMLInputElement | null, maxInput: HTMLInputElement | null }}
   */
  #getHiddenInputs() {
    const parent = this.closest('price-facet-component');

    return {
      minInput: parent?.querySelector('[ref="minInput"]') ?? null,
      maxInput: parent?.querySelector('[ref="maxInput"]') ?? null,
    };
  }

  #initializeThumbValues() {
    const { minInput, maxInput } = this.#getHiddenInputs();
    const { minRange, maxRange } = this.refs;

    let minVal = minInput?.value ? this.#parseMajorUnits(minInput.value) : this.#rangeMin;
    let maxVal = maxInput?.value ? this.#parseMajorUnits(maxInput.value) : this.#rangeMax;

    if (!minInput?.value && !maxInput?.value) {
      minVal = this.#minorToMajor(parseInt(this.dataset.currentMinMinor ?? '0', 10) || 0);
      maxVal = this.#minorToMajor(parseInt(this.dataset.currentMaxMinor ?? '0', 10) || 0);
    }

    minVal = this.#snapToStep(this.#clamp(minVal, this.#rangeMin, this.#rangeMax));
    maxVal = this.#snapToStep(this.#clamp(maxVal, this.#rangeMin, this.#rangeMax));

    if (minVal > maxVal) {
      minVal = maxVal;
    }

    minRange.value = String(minVal);
    maxRange.value = String(maxVal);
  }

  #onRangeInput = () => {
    this.#constrainThumbs();
    this.#updateUI();
  };

  #constrainThumbs() {
    const { minRange, maxRange } = this.refs;
    let minVal = this.#snapToStep(parseFloat(minRange.value));
    let maxVal = this.#snapToStep(parseFloat(maxRange.value));

    if (minVal > maxVal) {
      if (document.activeElement === minRange) {
        maxVal = minVal;
      } else {
        minVal = maxVal;
      }
    }

    minRange.value = String(minVal);
    maxRange.value = String(maxVal);
  }

  /**
   * @param {boolean} dispatchChange
   */
  #updateHiddenInputs(dispatchChange) {
    const { minRange, maxRange } = this.refs;
    const { minInput, maxInput } = this.#getHiddenInputs();

    if (!minInput || !maxInput) return;

    const minVal = this.#snapToStep(parseFloat(minRange.value));
    const maxVal = this.#snapToStep(parseFloat(maxRange.value));
    const atFullRange = minVal <= this.#rangeMin && maxVal >= this.#rangeMax;

    if (atFullRange) {
      minInput.value = '';
      maxInput.value = '';
      minInput.setAttribute('data-max', this.#formatInputValue(this.#rangeMax));
      maxInput.setAttribute('data-min', '0');
    } else {
      const minMinor = this.#toMinorUnits(minVal);
      const maxMinor = this.#toMinorUnits(maxVal);

      minInput.value =
        minVal <= this.#rangeMin
          ? ''
          : formatMoney(minMinor, this.#inputMoneyFormat, this.#currency);
      maxInput.value =
        maxVal >= this.#rangeMax
          ? ''
          : formatMoney(maxMinor, this.#inputMoneyFormat, this.#currency);

      minInput.setAttribute('data-max', maxInput.value || this.#formatInputValue(this.#rangeMax));
      maxInput.setAttribute('data-min', minInput.value || '0');
    }

    if (dispatchChange) {
      maxInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #updateUI() {
    const { minRange, maxRange, rangeFill, minLabel, maxLabel } = this.refs;
    const minVal = parseFloat(minRange.value);
    const maxVal = parseFloat(maxRange.value);
    const span = this.#rangeMax - this.#rangeMin;
    const lowerPercent = span > 0 ? ((minVal - this.#rangeMin) / span) * 100 : 0;
    const upperPercent = span > 0 ? ((maxVal - this.#rangeMin) / span) * 100 : 100;

    rangeFill.style.left = `${lowerPercent}%`;
    rangeFill.style.width = `${upperPercent - lowerPercent}%`;

    minRange.setAttribute('aria-valuemin', String(this.#rangeMin));
    minRange.setAttribute('aria-valuemax', String(this.#rangeMax));
    maxRange.setAttribute('aria-valuemin', String(this.#rangeMin));
    maxRange.setAttribute('aria-valuemax', String(this.#rangeMax));
    minRange.setAttribute('aria-valuenow', String(minVal));
    maxRange.setAttribute('aria-valuenow', String(maxVal));

    minLabel.textContent = this.#formatDisplay(minVal);
    maxLabel.textContent = this.#formatDisplay(maxVal);
  }

  /**
   * @param {number} majorUnits
   * @returns {string}
   */
  #formatInputValue(majorUnits) {
    return formatMoney(this.#toMinorUnits(majorUnits), this.#inputMoneyFormat, this.#currency);
  }

  /**
   * @param {number} majorUnits
   * @returns {string}
   */
  #formatDisplay(majorUnits) {
    return formatMoney(this.#toMinorUnits(majorUnits), this.#displayMoneyFormat, this.#currency);
  }

  /**
   * @param {number} value
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  #clamp(value, min, max) {
    return Math.max(min, Math.min(value, max));
  }
}

if (!customElements.get('price-slider-component')) {
  customElements.define('price-slider-component', PriceSliderComponent);
}
